import React, { Component } from "react";
import { Modal, GET, POST, PUT, DELETE, alert, t, currentLang } from "./CustomComponent.js";
import { toDateOnly, fmtDate, fmtMinutes, pruneParams, typeName } from "../utils/timeTracking.js";
import "../assets/css/time-tracking.css";

const BACKDATE_DAYS = 7;
const STEP_MIN = 5;
const MAX_END_MIN = 23 * 60 + 55;
const PROJECT_Q_DEBOUNCE_MS = 300;
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 / STEP_MIN }, (_, i) => String(i * STEP_MIN).padStart(2, "0"));

const parseHm = (value) => {
  const [h, m] = String(value || "0:0").split(":");
  return Number(h) * 60 + Number(m);
};
const minutesToHm = (min) => `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
const nowMinuteOfDay = () => {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
};
const addDays = (dateOnly, diff) => {
  const [y, m, d] = dateOnly.split("-").map(Number);
  return toDateOnly(new Date(y, m - 1, d + diff));
};
// รหัสโครงการแบบย่อ: 3 ตัวแรก…3 ตัวท้าย (เหมือนแอป)
const shortCode = (code) => (!code ? "-" : code.length > 8 ? `${code.slice(0, 3)}…${code.slice(-3)}` : code);
const fill = (text, vars) => Object.entries(vars).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, v), text);
const formatDuration = (min) => fill(t("pam_duration"), { h: Math.floor(min / 60), m: String(min % 60).padStart(2, "0") });

// เลือกเวลา ชม./นาที (นาทีทีละ 5) - ไม่ใช้ input type=time เพราะรูปแบบขึ้นกับ locale เบราว์เซอร์
const TimeSelect = ({ value, onChange, invalid, testId }) => {
  const [h, m] = String(value).split(":");
  const style = { width: "100%", border: `1px solid ${invalid ? "var(--tt-crit)" : "#dee2e6"}`, borderRadius: 12, padding: "8px 12px", background: "#fff", fontSize: 14 };
  return (
    <div className="d-flex align-items-center gap-1">
      <select style={style} value={h} onChange={(e) => onChange(`${e.target.value}:${m}`)} data-testid={`${testId}-h`}>
        {HOURS.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
      <b>:</b>
      <select style={style} value={MINUTES.includes(m) ? m : "00"} onChange={(e) => onChange(`${h}:${e.target.value}`)} data-testid={`${testId}-m`}>
        {MINUTES.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
    </div>
  );
};

const FIELD_INPUT = { width: "100%", border: "1px solid #dee2e6", borderRadius: 12, padding: "8px 12px", background: "#fff", color: "#212529", fontSize: 14 };
const LABEL = { fontSize: 12, color: "#6c757d", margin: "12px 0 4px", display: "block" };

/**
 * โมดัล "＋ เพิ่ม Activity" ของพนักงานสำนักงาน - ฟอร์ม/กติกาเดียวกับแอป (SNAPA-Mobile components/activity/ActivitySheet.tsx)
 *   GET    admin/project-activities/mine?date=            -> {date, total_minutes, items}
 *   GET    admin/project-activities/mine/projects?q=      -> {recent, items}
 *   GET    admin/project-activities/mine/types            -> []
 *   POST   admin/project-activities/mine | PUT .../mine/:uid | DELETE .../mine/:uid
 * @param {{show:boolean, onHide:Function, onSaved:Function, initialDate?:string, initialEditUid?:string}} props
 */
export default class ProjectActivityModal extends Component {
  constructor(props) {
    super(props);
    this.q_timer = null;
    this.flash_timer = null;
    this.state = this.initialState(props.initialDate);
  }

  initialState = (date) => ({
    view: "form", // form | project | tech
    technician: null, // ช่าง/พนักงานที่บันทึกแทน (null = ตัวเอง)
    tech_query: "",
    techs: null,
    loading_techs: false,
    date: date || toDateOnly(new Date()),
    project: null,
    no_project: false,
    no_project_name: "",
    types: [],
    type_uid: "",
    start: "08:00",
    end: "09:00",
    note: "",
    editing_uid: null,
    day: null,
    loading_day: false,
    saving: false,
    flash: "",
    now_min: nowMinuteOfDay(),
    project_query: "",
    projects: null,
    loading_projects: false,
    recent: [],
    has_tech: true, // false = ผู้บันทึกไม่ใช่ช่างและยังไม่ได้เลือกช่าง
  });

  componentDidMount() {
    if (this.props.show) this.onOpen();
  }

  componentDidUpdate(prev) {
    if (this.props.show && !prev.show) this.onOpen();
    if (!this.props.show && prev.show) this.clearTimers();
  }

  componentWillUnmount() {
    this.clearTimers();
  }

  clearTimers = () => {
    clearTimeout(this.q_timer);
    clearTimeout(this.flash_timer);
    clearInterval(this.now_timer);
  };

  onOpen = async () => {
    const date = this.props.initialDate || toDateOnly(new Date());
    this.setState(this.initialState(date));
    clearInterval(this.now_timer);
    this.now_timer = setInterval(() => this.setState({ now_min: nowMinuteOfDay() }), 20000);
    const [types, projects] = await Promise.all([
      GET("admin/project-activities/mine/types", this.techParams()),
      GET("admin/project-activities/mine/projects", this.techParams()),
    ]);
    this.setState({
      types: types?.status && Array.isArray(types.data) ? types.data : [],
      recent: projects?.status ? projects.data?.recent || [] : [],
      has_tech: projects?.status ? projects.data?.has_owner_technician !== false : true,
    });
    const day = await this.loadDay(date, true);
    if (this.props.initialEditUid && day) {
      const target = (day.items || []).find((a) => a.uid === this.props.initialEditUid && a.editable);
      if (target) this.startEdit(target);
    }
  };

  // ตั้งเวลาเริ่มต้นจากรายการล่าสุดของวัน (เริ่ม = end ของรายการล่าสุด)
  defaultTimes = (items) => {
    const lastEnd = items.reduce((max, a) => Math.max(max, parseHm(a.end_time)), -1);
    const startMin = lastEnd >= 0 ? Math.min(lastEnd, MAX_END_MIN - STEP_MIN) : 8 * 60;
    return { start: minutesToHm(startMin), end: minutesToHm(Math.min(startMin + 60, MAX_END_MIN)) };
  };

  loadDay = async (date, resetTimes) => {
    this.setState({ loading_day: true });
    const result = await GET("admin/project-activities/mine", { date, ...this.techParams() });
    if (!result?.status) {
      this.setState({ day: null, loading_day: false });
      return null;
    }
    const day = result.data || { date, total_minutes: 0, items: [] };
    this.setState({ day, loading_day: false, ...(resetTimes ? this.defaultTimes(day.items || []) : {}) });
    return day;
  };

  resetForm = (items) => {
    this.setState({ editing_uid: null, project: null, no_project: false, no_project_name: "", type_uid: "", note: "", ...this.defaultTimes(items) });
  };

  showFlash = (text) => {
    this.setState({ flash: text });
    clearTimeout(this.flash_timer);
    this.flash_timer = setTimeout(() => this.setState({ flash: "" }), 4000);
  };

  changeDate = (date) => {
    if (date === this.state.date) return;
    this.setState({ date, editing_uid: null, note: "", flash: "" });
    this.loadDay(date, true);
  };

  techParams = () => (this.state.technician?.uid ? { technician_uid: this.state.technician.uid } : {});

  // ---------------------------------------------------------------- technician picker
  openTechPicker = () => {
    if (this.state.editing_uid) return; // PUT เปลี่ยนเจ้าของไม่ได้
    this.setState({ view: "tech", tech_query: "" }, () => this.searchTechs(""));
  };

  searchTechs = async (q) => {
    this.setState({ loading_techs: true });
    const result = await GET("admin/project-activities/mine/technicians", pruneParams({ q: q.trim() }));
    if (q !== this.state.tech_query) return;
    this.setState({ techs: result?.status && Array.isArray(result.data) ? result.data : [], loading_techs: false });
  };

  setTechQuery = (value) => {
    this.setState({ tech_query: value });
    clearTimeout(this.q_timer);
    this.q_timer = setTimeout(() => this.searchTechs(value), value ? PROJECT_Q_DEBOUNCE_MS : 0);
  };

  // เปลี่ยนช่าง: โหลดรายการของวัน/ประเภท/โครงการล่าสุดของเจ้าของใหม่ ประเภทที่เลือกไว้แต่ไม่อยู่ในรายการใหม่ถูกล้าง
  pickTechnician = (technician) => {
    this.setState({ technician, view: "form", editing_uid: null, note: "", flash: "" }, async () => {
      const [types, projects] = await Promise.all([
        GET("admin/project-activities/mine/types", this.techParams()),
        GET("admin/project-activities/mine/projects", this.techParams()),
      ]);
      const list = types?.status && Array.isArray(types.data) ? types.data : [];
      this.setState((prev) => ({
        types: list,
        type_uid: list.some((x) => x.uid === prev.type_uid) ? prev.type_uid : "",
        recent: projects?.status ? projects.data?.recent || [] : prev.recent,
        has_tech: projects?.status ? projects.data?.has_owner_technician !== false : prev.has_tech,
        // โครงการที่เลือกไว้ต้องอยู่ในชุดของช่างใหม่ ไม่งั้นล้างทิ้ง
        project: prev.project && (projects?.status ? (projects.data?.items || []).some((x) => x.uid === prev.project.uid) : false) ? prev.project : null,
      }));
      this.loadDay(this.state.date, true);
    });
  };

  // ---------------------------------------------------------------- project picker
  openProjectPicker = () => {
    if (!this.state.has_tech) return;
    this.setState({ view: "project", project_query: "" }, () => this.searchProjects(""));
  };

  searchProjects = async (q) => {
    this.setState({ loading_projects: true });
    const result = await GET("admin/project-activities/mine/projects", pruneParams({ q: q.trim(), ...this.techParams() }));
    if (q !== this.state.project_query) return; // มีคำค้นใหม่กว่าแล้ว
    this.setState({
      projects: result?.status ? result.data : { recent: [], items: [] },
      loading_projects: false,
      ...(result?.status && !q.trim() ? { recent: result.data?.recent || [] } : {}),
    });
  };

  setProjectQuery = (value) => {
    this.setState({ project_query: value });
    clearTimeout(this.q_timer);
    this.q_timer = setTimeout(() => this.searchProjects(value), value ? PROJECT_Q_DEBOUNCE_MS : 0);
  };

  pickProject = (project) => this.setState({ project, view: "form" });

  // ---------------------------------------------------------------- validation (กติกาเดียวกับแอป, backend ตรวจซ้ำ)
  items = () => this.state.day?.items || [];

  typeOptions = () => {
    const { types, editing_uid } = this.state;
    const editingType = editing_uid ? this.items().find((a) => a.uid === editing_uid)?.activity_type : null;
    // ประเภทที่ถูกปิดใช้งานภายหลังไม่อยู่ใน master แล้ว - ต่อไว้ท้ายรายการ ไม่ให้ค่าหาย
    return editingType && !types.some((x) => x.uid === editingType.uid) ? [...types, { ...editingType, note_required: false }] : types;
  };

  timeProblem = () => {
    const { start, end, date, now_min, editing_uid } = this.state;
    const startMin = parseHm(start);
    const endMin = parseHm(end);
    if (endMin <= startMin) return t("pam_err_end_after_start");
    if (date === toDateOnly(new Date()) && endMin > now_min) return fill(t("pam_err_future_time"), { now: minutesToHm(now_min) });
    const clash = this.items().find((a) => a.uid !== editing_uid && startMin < parseHm(a.end_time) && endMin > parseHm(a.start_time));
    if (clash) return fill(t("pam_err_overlap"), { start: clash.start_time, end: clash.end_time, type: typeName(clash.activity_type, currentLang()) });
    return "";
  };

  // ---------------------------------------------------------------- actions
  save = async () => {
    const { date, project, no_project, no_project_name, type_uid, start, end, note, editing_uid } = this.state;
    this.setState({ saving: true });
    const body = {
      work_date: date,
      ...(!editing_uid && this.state.technician?.uid ? { technician_uid: this.state.technician.uid } : {}),
      ...(no_project ? { no_project: true, project_name_text: no_project_name.trim() } : { project_uid: project?.uid }),
      activity_type_uid: type_uid,
      start_time: start,
      end_time: end,
      // services/api.js ตัด "" ออกจาก body ส่วน "-" ถูกแปลงเป็น "" - ตอนแก้ต้องส่ง "-" เพื่อล้างหมายเหตุเดิม (ไม่ส่ง = backend คงค่าเดิม)
      note: note.trim() || (editing_uid ? "-" : undefined),
    };
    const result = editing_uid ? await PUT(`admin/project-activities/mine/${editing_uid}`, body) : await POST("admin/project-activities/mine", body);
    this.setState({ saving: false });
    if (!result?.status) return; // services/api.js แสดงเหตุผลจาก backend ให้แล้ว
    const day = await this.loadDay(date, false);
    this.resetForm(day?.items || []);
    this.showFlash(fill(t("pam_saved"), { start, end, value: formatDuration(parseHm(end) - parseHm(start)) }));
    this.props.onSaved?.();
  };

  startEdit = (a) => {
    const noProject = !a.project || !!a.is_no_project;
    this.setState({
      editing_uid: a.uid,
      no_project: noProject,
      no_project_name: noProject ? a.project_name_text || "" : "",
      project: noProject ? null : a.project,
      type_uid: a.activity_type?.uid || "",
      start: a.start_time,
      end: a.end_time,
      note: a.note || "",
      flash: "",
    });
  };

  confirmDelete = async (a) => {
    const label = a.project?.project_code || a.project?.project_name || a.project_name_text || "";
    const ok = await alert("", "warning", t("pam_delete_confirm_title"), fill(t("pam_delete_confirm_desc"), { project: label, range: `${a.start_time}–${a.end_time}` }), t("delete"), t("cancel"));
    if (!ok) return;
    const result = await DELETE(`admin/project-activities/mine/${a.uid}`);
    if (!result?.status) return;
    const day = await this.loadDay(this.state.date, false);
    this.resetForm(day?.items || []);
    this.showFlash(t("pam_deleted"));
    this.props.onSaved?.();
  };

  // ---------------------------------------------------------------- render
  renderProjectPicker() {
    const { project_query, projects, loading_projects } = this.state;
    return (
      <div>
        <input style={FIELD_INPUT} value={project_query} autoFocus placeholder={`🔍 ${t("pam_search_placeholder")}`} onChange={(e) => this.setProjectQuery(e.target.value)} data-testid="pam-project-search" />
        <div className="tt-mini my-2">{t("pam_pick_project_hint")}</div>
        {loading_projects ? <div className="tt-mini py-3">{t("loading")}</div> : null}
        {!loading_projects && projects && !projects.items?.length ? <div className="tt-mini py-3">{t("pam_no_projects")}</div> : null}
        {!loading_projects && projects ? (
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {(projects.items || []).map((p) => (
              <div key={p.uid} role="button" className="py-2 px-1" style={{ borderBottom: "1px solid var(--tt-line)", cursor: "pointer" }} onClick={() => this.pickProject(p)} data-testid="pam-project-row">
                <b className="tt-mono" style={{ fontSize: 12 }}>{p.project_code || "-"}</b>
                <div style={{ fontSize: 12.5 }}>{p.project_name || ""}</div>
                {p.customer_name ? <div className="tt-mini">{p.customer_name}</div> : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  renderTechPicker() {
    const { tech_query, techs, loading_techs } = this.state;
    return (
      <div>
        <input style={FIELD_INPUT} value={tech_query} autoFocus placeholder={`🔍 ${t("pam_tech_search")}`} onChange={(e) => this.setTechQuery(e.target.value)} data-testid="pam-tech-search" />
        <div role="button" className="py-2 px-1" style={{ borderBottom: "1px solid var(--tt-line)", cursor: "pointer" }} onClick={() => this.pickTechnician(null)} data-testid="pam-tech-self"><b>{t("pam_tech_self")}</b></div>
        {loading_techs ? <div className="tt-mini py-3">{t("loading")}</div> : null}
        {!loading_techs && techs && !techs.length ? <div className="tt-mini py-3">{t("pam_tech_none")}</div> : null}
        {!loading_techs && techs ? (
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {techs.map((x) => (
              <div key={x.uid} role="button" className="py-2 px-1" style={{ borderBottom: "1px solid var(--tt-line)", cursor: "pointer" }} onClick={() => this.pickTechnician(x)} data-testid="pam-tech-row">
                <b className="tt-mono" style={{ fontSize: 12 }}>{x.code || "-"}</b> · {x.name}
                {x.department_name ? <div className="tt-mini">{x.department_name}</div> : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  renderDayList() {
    const { day, loading_day, editing_uid, date } = this.state;
    const items = this.items();
    const lang = currentLang();
    const today = toDateOnly(new Date());
    const dayLabel = date === today ? t("today") : fmtDate(date);
    return (
      <div style={{ marginTop: 16, paddingTop: 10, borderTop: "1px dashed var(--tt-line)" }}>
        {loading_day ? <div className="tt-mini">{t("loading")}</div> : null}
        {!loading_day && items.length ? (
          <div className="d-flex justify-content-between mb-1">
            <b style={{ fontSize: 12 }}>{fill(t("pam_list_title"), { count: items.length, day: dayLabel })}</b>
            <span className="tt-mini">{t("pam_day_total")} <b className="tt-mono" style={{ color: "var(--tt-purple)" }}>{fmtMinutes(day?.total_minutes)}</b></span>
          </div>
        ) : null}
        {!loading_day && !items.length ? <div className="tt-mini">{fill(t("pam_list_empty"), { day: dayLabel })}</div> : null}
        {items.map((a) => (
          <div
            key={a.uid}
            role={a.editable ? "button" : undefined}
            className="d-flex justify-content-between align-items-center py-1 px-1"
            style={{ borderBottom: "1px solid var(--tt-surface-2)", fontSize: 12, cursor: a.editable ? "pointer" : "default", background: a.uid === editing_uid ? "var(--tt-brand-soft)" : undefined }}
            onClick={() => a.editable && this.startEdit(a)}
            data-testid="pam-item"
          >
            <span>
              <b className="tt-mono">{a.start_time}–{a.end_time}</b>
              {" · "}{a.project ? shortCode(a.project.project_code) : a.project_name_text || t("pam_no_project_badge")}
              {" · "}{typeName(a.activity_type, lang)}
              {a.note ? <span className="tt-mini"> · {a.note}</span> : null}
              {a.recorded_by?.name ? <span className="tt-mini"> · {fill(t("pam_recorded_by"), { name: a.recorded_by.name })}</span> : null}
            </span>
            <span className="tt-mini">{fmtMinutes(a.duration_min)} {a.editable ? "✎" : ""}</span>
          </div>
        ))}
      </div>
    );
  }

  renderForm() {
    const { date, project, no_project, no_project_name, type_uid, start, end, note, editing_uid, saving, flash, recent, technician, has_tech } = this.state;
    const lang = currentLang();
    const today = toDateOnly(new Date());
    const dateOptions = Array.from({ length: BACKDATE_DAYS + 1 }, (_, i) => addDays(today, -i));
    const typeOptions = this.typeOptions();
    const selectedType = typeOptions.find((x) => x.uid === type_uid);
    const noteRequired = !!selectedType?.note_required;
    const problem = this.timeProblem();
    // ไม่มีโครงการ = ไม่ผูก project_technicians จึงใช้ได้แม้ยังไม่เลือกช่าง ; เลือกจากรายการต้องมีช่างเจ้าของรายการ
    const projectReady = no_project ? !!no_project_name.trim() : has_tech && !!project;
    const canSave = projectReady && !!type_uid && !problem && (!noteRequired || !!note.trim()) && !saving;
    const duration = parseHm(end) - parseHm(start);

    return (
      <div>
        <label style={{ ...LABEL, marginTop: 0 }}>{t("pam_field_tech")}</label>
        <div className="d-flex align-items-center gap-1">
          <div role="button" className="d-flex align-items-center" style={{ ...FIELD_INPUT, flex: 1, cursor: editing_uid ? "not-allowed" : "pointer", opacity: editing_uid ? 0.6 : 1, background: editing_uid ? "var(--tt-surface-2)" : "#fff" }} aria-disabled={!!editing_uid} onClick={this.openTechPicker} data-testid="pam-tech-field">
            <div style={{ flex: 1, minWidth: 0 }} className="text-truncate">
              {technician ? <><b className="tt-mono" style={{ fontSize: 12 }}>{technician.code || "-"}</b> · {technician.name}</> : <span className="tt-mini">{t("pam_tech_self")}</span>}
            </div>
            <span className="tt-mini">›</span>
          </div>
          {technician && !editing_uid ? <button type="button" className="tt-btn tt-sm" title={t("pam_tech_clear")} onClick={() => this.pickTechnician(null)} data-testid="pam-tech-clear">✕</button> : null}
        </div>

        <label style={LABEL}>{t("pam_field_date")} <span style={{ color: "var(--tt-crit)" }}>*</span></label>
        <select style={FIELD_INPUT} value={date} onChange={(e) => this.changeDate(e.target.value)} data-testid="pam-date">
          {dateOptions.map((d) => <option key={d} value={d}>{d === today ? `${t("today")} · ${fmtDate(d)}` : fmtDate(d)}</option>)}
        </select>

        <label style={LABEL}>{t("pam_field_project")} <span style={{ color: "var(--tt-crit)" }}>*</span></label>
        {no_project ? (
          <input style={FIELD_INPUT} value={no_project_name} maxLength={200} placeholder={t("pam_no_project_placeholder")} onChange={(e) => this.setState({ no_project_name: e.target.value })} data-testid="pam-no-project-name" />
        ) : !has_tech ? (
          <div className="px-3 py-2" style={{ borderRadius: 10, background: "var(--tt-crit-soft)", color: "var(--tt-crit)", fontSize: 13 }} data-testid="pam-need-tech">{t("pam_need_tech")}</div>
        ) : (
          <div role="button" className="d-flex align-items-center" style={{ ...FIELD_INPUT, cursor: "pointer" }} onClick={this.openProjectPicker} data-testid="pam-project-field">
            <div style={{ flex: 1, minWidth: 0 }}>
              {project ? (
                <>
                  <b className="tt-mono" style={{ fontSize: 13 }}>{project.project_code || "-"}</b>
                  <div className="tt-mini text-truncate">{[project.project_name, project.customer_name].filter(Boolean).join(" · ")}</div>
                </>
              ) : (
                <span className="tt-mini">{t("pam_project_placeholder")}</span>
              )}
            </div>
            <span className="tt-mini">›</span>
          </div>
        )}
        <label className="d-flex align-items-center gap-2 mt-2" style={{ fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={no_project} onChange={(e) => this.setState({ no_project: e.target.checked })} data-testid="pam-no-project-check" />
          {t("pam_no_project_check")}
        </label>
        {recent.length && has_tech && !no_project ? (
          <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
            <span className="tt-mini">{t("pam_recent")}</span>
            {recent.map((p) => (
              <button key={p.uid} type="button" className="tt-btn tt-sm" style={{ borderRadius: 999, fontWeight: 500 }} title={p.project_name || ""} onClick={() => this.setState({ project: p })}>{shortCode(p.project_code)}</button>
            ))}
          </div>
        ) : null}

        <label style={LABEL}>{t("pam_field_type")} <span style={{ color: "var(--tt-crit)" }}>*</span></label>
        <select style={FIELD_INPUT} value={type_uid} onChange={(e) => this.setState({ type_uid: e.target.value })} data-testid="pam-type">
          <option value="">{t("pam_type_placeholder")}</option>
          {typeOptions.map((x) => <option key={x.uid} value={x.uid}>{typeName(x, lang)}</option>)}
        </select>
        {!typeOptions.length ? <div className="tt-mini mt-1">{t("pam_no_types")}</div> : null}

        <label style={LABEL}>{t("pam_field_time")} <span style={{ color: "var(--tt-crit)" }}>*</span></label>
        <div className="d-flex align-items-center gap-2">
          <div style={{ flex: 1 }}><TimeSelect value={start} invalid={!!problem} testId="pam-start" onChange={(v) => this.setState({ start: v })} /></div>
          <span className="tt-mini">{t("pam_to")}</span>
          <div style={{ flex: 1 }}><TimeSelect value={end} invalid={!!problem} testId="pam-end" onChange={(v) => this.setState({ end: v })} /></div>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-2 px-3 py-2" style={{ borderRadius: 10, background: problem ? "var(--tt-crit-soft)" : "var(--tt-brand-soft)", color: problem ? "var(--tt-crit)" : "var(--tt-brand-dark)" }} data-testid="pam-total">
          {problem ? <b style={{ fontSize: 13 }}>⚠ {problem}</b> : (
            <>
              <b style={{ fontSize: 13 }}>{t("pam_total_label")}</b>
              <b className="tt-mono" style={{ fontSize: 17 }}>{formatDuration(duration)}</b>
            </>
          )}
        </div>

        <label style={LABEL}>
          {t("note")} {noteRequired && selectedType ? <span style={{ color: "var(--tt-crit)" }}>{fill(t("pam_note_required_hint"), { name: typeName(selectedType, lang) })}</span> : null}
        </label>
        <textarea style={{ ...FIELD_INPUT, minHeight: 60 }} rows={2} maxLength={500} value={note} placeholder={t("pam_note_placeholder")} onChange={(e) => this.setState({ note: e.target.value })} data-testid="pam-note" />

        {flash ? <div className="mt-2" style={{ color: "var(--tt-ok)", fontSize: 12, fontWeight: 700 }} data-testid="pam-flash">✓ {flash}</div> : null}

        <div className="d-flex gap-2 mt-3">
          {editing_uid ? (
            <>
              <button type="button" className="tt-btn" onClick={() => this.resetForm(this.items())}>{t("pam_cancel_edit")}</button>
              <button type="button" className="tt-btn tt-rj" onClick={() => this.confirmDelete(this.items().find((a) => a.uid === editing_uid))} data-testid="pam-delete">{t("pam_delete_item")}</button>
            </>
          ) : (
            <button type="button" className="tt-btn" onClick={this.props.onHide}>{t("cancel")}</button>
          )}
          <button type="button" className="tt-btn tt-pri ms-auto" style={{ opacity: canSave ? 1 : 0.45 }} disabled={!canSave} onClick={this.save} data-testid="pam-save">{t("save")}</button>
        </div>

        {this.renderDayList()}
      </div>
    );
  }

  render() {
    const { show, onHide } = this.props;
    const { view, editing_uid } = this.state;
    return (
      <Modal show={show} onHide={onHide} centered contentClassName="border-0 rounded-4 shadow-lg">
        <Modal.Header className="border-bottom border-light px-4 py-3 bg-white" closeButton>
          <Modal.Title as="h5" className="fw-bold mb-0 text-dark" style={{ fontSize: 17 }}>
            {view !== "form" ? (
              <>
                <button type="button" className="tt-btn tt-sm me-2" onClick={() => this.setState({ view: "form" })}>‹ {t("pam_back")}</button>
                {view === "tech" ? t("pam_field_tech") : t("pam_pick_project")}
              </>
            ) : editing_uid ? t("pam_title_edit") : `＋ ${t("pa_add_activity")}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 bg-white">
          <div className="tt-page">{view === "project" ? this.renderProjectPicker() : view === "tech" ? this.renderTechPicker() : this.renderForm()}</div>
        </Modal.Body>
      </Modal>
    );
  }
}
